import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import API from "../api/axios";
import useSocket from "../hooks/useSocket";
import {
  MdWarning, MdSpeed, MdWifiOff,
  MdTimeline, MdNotificationsActive,
} from "react-icons/md";
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const Anomalies = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const { monitoringData } = useSocket();

  useEffect(() => {
    fetchAnomalies();
    fetchStats();
  }, []);

  const fetchAnomalies = async () => {
    try {
      const res = await API.get("/anomalies");
      setAnomalies(res.data);
    } catch {
      setAnomalies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await API.get("/anomalies/stats");
      setStats(res.data);
    } catch {
      setStats(null);
    }
  };

  // Chart data from monitoring
  const chartData = monitoringData.map((d) => ({
    name: d.name,
    latency: d.latency || 0,
  }));

  const filtered = anomalies.filter((a) => {
    if (filter === "ALL") return true;
    return a.type === filter;
  });

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Anomaly Detection
        </h2>
        <p className="text-sm text-gray-500">
          AI-powered network anomaly detection using statistical analysis
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
            <MdWarning size={28} className="text-orange-500" />
            <div>
              <p className="text-xs text-gray-500">Total Anomalies</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
            <MdSpeed size={28} className="text-red-500" />
            <div>
              <p className="text-xs text-gray-500">Latency Spikes</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.latencySpikes}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
            <MdWifiOff size={28} className="text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Offline Streaks</p>
              <p className="text-2xl font-bold text-gray-800">
                {stats.offlineStreaks}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
            <MdNotificationsActive size={28} className="text-blue-500" />
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-2xl font-bold text-blue-600">{stats.today}</p>
            </div>
          </div>
        </div>
      )}

      {/* Live Latency Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MdTimeline size={20} className="text-blue-500" />
            <h3 className="text-md font-semibold text-gray-700">
              Live Device Latency Overview
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="latency"
                stroke="#dc2626"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Latency (ms)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-xl shadow p-4 mb-4 flex gap-2">
        {["ALL", "LATENCY_SPIKE", "OFFLINE_STREAK"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              filter === f
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Anomalies Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Loading anomalies...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <MdWarning size={40} className="mx-auto mb-2 text-gray-300" />
            <p>No anomalies detected</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Device</th>
                <th className="px-6 py-3 text-left">Description</th>
                <th className="px-6 py-3 text-left">Latency</th>
                <th className="px-6 py-3 text-left">Baseline</th>
                <th className="px-6 py-3 text-left">Std Dev</th>
                <th className="px-6 py-3 text-left">Detected At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((anomaly) => (
                <tr key={anomaly.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        anomaly.type === "LATENCY_SPIKE"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {anomaly.type === "LATENCY_SPIKE" ? (
                        <MdSpeed size={14} />
                      ) : (
                        <MdWifiOff size={14} />
                      )}
                      {anomaly.type.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {anomaly.device?.name}
                  </td>
                  <td className="px-6 py-4 text-gray-600 max-w-xs">
                    {anomaly.description}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {anomaly.latency ? `${anomaly.latency}ms` : "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {anomaly.baseline ? `${anomaly.baseline}ms` : "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {anomaly.deviation ? `±${anomaly.deviation}ms` : "—"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(anomaly.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default Anomalies;